import { IAmount } from "./fee";
import { ISigningInfo } from "./signing";
import * as _ from "lodash";

// See https://api.fat.sparkasse.at/fat2/rest/community/doc/presto_apidoc.html#a_2.24
export interface IAccountNumber {
    iban?: string;
    bic?: string;
    number?: string;
    bankCode?: string;
    countryCode?: string;
}

type IAccountAdditionalData = {
    color?: string;
    accountColor?: {
        name?: string;
        hexCodeLight?: string;
        hexCodeDark?: string;
        acessibilityLabel?: string;
        label?: string;
        hexCode?: string;
    };
    position?: number,
    hidden?: boolean;
    upcomingOrderDays?: number;
    colorCode?: string;
};

export interface IAccount {
    id: string;
    accountno: IAccountNumber;
    currency?: string;
    alias?: string;
    description: string;
    balance?: IAmount;
    disposable?: IAmount;
    loan?: {
        nextRateAmount?: IAmount;
        nextRateDate?: string;
        nextRateType?: string;
    };
    subType?: string;
    type?: string;
    typeI18N?: string;
    productI18N?: string;
    product?: string;
    features?: string[];
    flags?: string[];
    usableFor?: string[];
    statementCreation?: {
        periodicity: string;
    };
    additionalData?: IAccountAdditionalData;
}

export interface IPutAccountResponse {
    account: IAccount;
    signInfo: ISigningInfo;
}

export interface ISecuritiesAccount {
    accountno: {
        number: string,
        bankCode: number,
        countryCode: string,
        "at-institute": number
    };
    alias: string;
    "at-institute": number;
    "at-orderLimit": IAmount;
    "at-otherSettlementAccountsIban": {
        iban: string,
        currency: string
    }[];
    balance: IAmount;
    description: string;
    flags: string[];
    id: string;
    settlementAccount: {
        number: string,
        bankCode: number,
        countryCode: string,
        "at-institute": number
    };
    settlementAccountCurrency: string;
    settlementAccountIban: string;
    signRole: string;
    subSecAccounts: {
        id: string,
        name: string,
        titles: any[];
    }[];
    tradeableStatus: string;
    tradingActivationAllowed: true;
    tradingRefusalReasons: any[];
}

export interface IBuildingsAccount {
    accountno: {
        iban: string;
        bic: string;
    };
    bic: string;
    iban: string;
    balance: IAmount;
    currency: string;
    precision: number;
    value: number;
    contractHolders: string[];
    description: string;
    flags: string[];
    id: string;
    loan: {
        loanAmount: IAmount;
    };
    loanAmount: IAmount;
    loanInstallment: IAmount;
    product: string;
    productI18N: string;
    saving: {
        agreedMonthlySavings: IAmount;
        expiryDate: Date;
    };
    savingsFrequency: string;
    state: string;
    status: string;
    type: string;
    codePossibleFunction: "payout" | "payoutLegalGuardian";
}

export interface IBuildingsProlongationInfo {
    prolongationDetails: {
        interestRate: string;
        interestRateCode: "F" | "V";
        isGovernmentBonus: "J" | "N";
        startDate: Date;
        endDate: Date;
        iban: string;
        customerDetails: [
            {
                customerId: string,
                shortName: string,
                lastName: string,
                firstName: string,
                dateOfBirth: Date,
                street: string,
                zipCode: string,
                city: string,
                customerRoleCode: "VI" | "GV" | "WVI",
                customerRoleText: string
            }
        ];
    };
}

export interface IBuildingsPayoutInfo {
    contractExpiryDate: string;
    contractNumber: string;
    contractOwners: [{
        customerId: string;
        customerShortName: string;
        title: string;
        firstName: string;
        lastName: string;
        dateOfBirth: string;
        street: string;
        zipCode: string;
        city: string;
        customerRoleCode: "VI" | "GV" | "WVI";
        customerRoleText: string;

    }];
    payoutAccounts: IAccount[];
}


export interface ILinkAccount {
    accountId: string;
}

export interface ILinkedAccounts {
    linkedAccounts: ILinkAccount[];
}

export interface ILinkableAccounts {
    linkableAccounts: ILinkAccount[];
}

export interface ILinkageResponse {
    accountLinkageType: {
        linkAccounts?: Array<{
            accountId: string;
        }>;
        unlinkAccounts?: Array<{
            accountId: string;
        }>;
    };
    signInfo: ISigningInfo;
}

export function supportsPockets(account: IAccount) {
    return account.features && _.includes(account.features, "savingEnvelopes");
}

export interface ISavingsAccount extends IAccount {
    saving?: {
        minimumBalance: IAmount;
        ownTransferMaximum: IAmount;
    };
}


// WEBPACK FOOTER //
// ./src/types/account.ts